import { COLLECTIONS, type Collection, type LocalStore } from "./types";

/**
 * The subset of a SQLite connection we need. @capacitor-community/sqlite's SQLiteDBConnection
 * matches this shape; tests use Node's built-in sqlite behind the same interface.
 */
export interface SqlConnection {
  execute(statements: string): Promise<unknown>;
  run(statement: string, values?: unknown[]): Promise<unknown>;
  query(statement: string, values?: unknown[]): Promise<{ values?: Record<string, unknown>[] }>;
}

const SCHEMA = `
CREATE TABLE IF NOT EXISTS records (collection TEXT NOT NULL, id TEXT NOT NULL, json TEXT NOT NULL, PRIMARY KEY (collection, id));
CREATE TABLE IF NOT EXISTS meta (key TEXT PRIMARY KEY NOT NULL, json TEXT NOT NULL);
`;

/**
 * Native storage. SQLite files live in the app's private data folder, which iOS and Android
 * never evict (unlike webview IndexedDB under storage pressure).
 */
export async function createSqliteStore(conn: SqlConnection): Promise<LocalStore> {
  await conn.execute(SCHEMA);
  return {
    all: async <T>(c: Collection) => {
      const res = await conn.query("SELECT json FROM records WHERE collection = ?", [c]);
      return (res.values ?? []).map((r) => JSON.parse(r.json as string)) as T[];
    },
    putMany: async (c, records) => {
      for (const r of records)
        await conn.run("INSERT OR REPLACE INTO records (collection, id, json) VALUES (?, ?, ?)", [
          c,
          r.id,
          JSON.stringify(r),
        ]);
    },
    getMeta: async <T>(key: string) => {
      const res = await conn.query("SELECT json FROM meta WHERE key = ?", [key]);
      const row = res.values?.[0];
      return row ? (JSON.parse(row.json as string) as T) : undefined;
    },
    setMeta: async (key, value) => {
      await conn.run("INSERT OR REPLACE INTO meta (key, json) VALUES (?, ?)", [
        key,
        JSON.stringify(value),
      ]);
    },
    clear: async () => {
      await conn.run(
        `DELETE FROM records WHERE collection IN (${COLLECTIONS.map(() => "?").join(",")})`,
        [...COLLECTIONS],
      );
      await conn.run("DELETE FROM meta");
    },
  };
}

/** Open the Capacitor SQLite database (native only; imported lazily so the web bundle stays small). */
export async function openCapacitorSqlite(): Promise<SqlConnection> {
  const { CapacitorSQLite, SQLiteConnection } = await import("@capacitor-community/sqlite");
  const sqlite = new SQLiteConnection(CapacitorSQLite);
  const exists = (await sqlite.isConnection("hexling", false)).result;
  const db = exists
    ? await sqlite.retrieveConnection("hexling", false)
    : await sqlite.createConnection("hexling", false, "no-encryption", 1, false);
  await db.open();
  return {
    execute: (s) => db.execute(s),
    run: (s, v) => db.run(s, v as unknown[]),
    query: (s, v) => db.query(s, v as unknown[]),
  };
}
